import {
  normalizeTokenResponseScope,
  oauthAuth,
} from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const twitch = defineConnector({
  identifier: "twitch",
  name: "Twitch",
  tagLine: "Live streaming and gaming",
  logo,
  protocol: "http",
  tags: ["communication"],
  defaultUrlPattern: () => "https://api\\.twitch\\.tv/.*",
  auth: (app) =>
    oauthAuth.withHeaders([{ name: "Client-Id", value: app.config.clientId }]),
  steps: [
    {
      title: "Register a Twitch application",
      items: [
        {
          kind: "markdown",
          content:
            "Go to the [Twitch Developer Console](https://dev.twitch.tv/console/apps) and click **Register Your Application**.",
        },
      ],
    },
    {
      title: "Fill out the details",
      items: [
        {
          kind: "markdown",
          content: "Copy and paste the following into the form:",
        },
        {
          kind: "copyable",
          label: "OAuth Redirect URL",
          value: "{API_URL}/connected-apps/oauth/twitch/callback",
        },
        {
          kind: "markdown",
          content:
            "Set the **Category** to a relevant option, then click **Create**.",
        },
      ],
    },
    {
      title: "Enter your client credentials",
      items: [
        {
          kind: "markdown",
          content:
            "On the application management page, copy the **Client ID** and generate a **New Secret**.",
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client ID (for API requests)",
          required: true,
          target: { kind: "configuration", field: "clientId" },
          helperText:
            "Enter the same Client ID again. Twitch requires it as a header on every API request.",
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Define scopes",
      items: [
        {
          kind: "markdown",
          content:
            "Add [scopes](https://dev.twitch.tv/docs/authentication/scopes/) in a space-separated list below.",
        },
        {
          kind: "input",
          label: "Scopes (optional)",
          defaultValue: "user:read:email",
          helperText: "Space-separated list of desired scopes.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Authorize your Twitch account",
      items: [
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () => "https://id.twitch.tv/oauth2/authorize",
    tokenEndpoint: () => "https://id.twitch.tv/oauth2/token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    normalizeTokenResponse: normalizeTokenResponseScope,
    clientCredentialsFrom: "db",
    scopes: () => ["user:read:email"],
  },
  connectionTest: {
    protocol: "http",
    request: () => "https://api.twitch.tv/helix/users",
  },
});
